import { ExcelTemplate } from "../../../interfaces";
import { WorkBook } from "xlsx-js-style";
import * as XLSX from 'xlsx-js-style';
import { getPreparedData } from "./mapping";
import { setColumnWidth, withHeaderStyling } from "../../../utils";
import { getPreparedDataInternal } from "../internal/mapping";
import { FundStrategyExportExcelData, RoundsFS } from "../../../../models/fund-strategy/pojo/fund-strategy-export-classes";
import { fitToColumn } from "../../../fitToColumn";

export class FundStrategyExternalTemplate implements ExcelTemplate {
  export(data: FundStrategyExportExcelData): WorkBook {
    const book = XLSX.utils.book_new();
    const sheet = this.getSheet(data);
    XLSX.utils.book_append_sheet(book, sheet, 'Groups');
    const uniqueSheetNames = new Set<string>();
    data.groups.forEach((gr) => {
      const groupName = gr.controls.name.value + ' ';
      gr.controls.subGroups.controls.forEach((sb) => {
        const baseName = groupName + sb.controls.name.value;
        const uniqueName = this.getUniqueSheetName(baseName, uniqueSheetNames);
        uniqueSheetNames.add(uniqueName);

        const sheetInternal = this.getSheetInternal(sb.controls.roundsData.value as RoundsFS[]);
        XLSX.utils.book_append_sheet(book, sheetInternal, uniqueName);
      });
    });
    return book;
  }

  private getSheet(data: FundStrategyExportExcelData): XLSX.WorkSheet {
    const preparedData = getPreparedData(data);
    const sheet = {
      ...XLSX.utils.aoa_to_sheet(preparedData),
      '!cols': fitToColumn(preparedData),
      '!merges': [
        { s: { r: 23, c: 4 }, e: { r: 23, c: 5 } },
      ],
    };
    const whiteSpaceIndices = [2, 6];
    const longColumns = [3, 4, 5, 7, 8, 9, 10, 11];
    const mediumColumns = [1];
    const superLargeColumns = [0];
    whiteSpaceIndices.forEach((index) => setColumnWidth(sheet, index, 1));
    longColumns.forEach((index) => setColumnWidth(sheet, index, 12));
    superLargeColumns.forEach((index) => setColumnWidth(sheet, index, 36));
    mediumColumns.forEach((index) => setColumnWidth(sheet, index, 18));

    return (sheet);
  }

  private getSheetInternal(data: RoundsFS[]): XLSX.WorkSheet {
    const preparedData = getPreparedDataInternal(data);
    const sheet = {
      ...XLSX.utils.aoa_to_sheet(preparedData),
      '!cols': fitToColumn(preparedData),
    };
    const whiteSpaceIndices = [6, 10, 14, 16];
    whiteSpaceIndices.forEach((index) => setColumnWidth(sheet, index, 1));
    return withHeaderStyling(sheet);
  }

  /**
 * Generates a unique sheet name for an Excel workbook.
 *
 * Excel sheet names must be unique within a workbook, and this function ensures that
 * the returned name does not conflict with any existing names provided in the `usedNames` set.
 *
 * If the base name is already used, the function will append an incrementing number (e.g., "Sheet", "Sheet1", "Sheet2", ...)
 * until it finds an unused name.
 *
 * @param base - The desired base name for the sheet (will be truncated if needed).
 * @param usedNames - A Set containing all sheet names already in use.
 * @param i - The current index used for recursion (default is 0).
 * @returns A unique sheet name that does not conflict with existing names.
 *
 * @example
 * const used = new Set(['Sheet', 'Sheet1']);
 * const name = getUniqueSheetName('Sheet', used); // returns "Sheet2"
 */
  private getUniqueSheetName(base: string, usedNames: Set<string>, i = 0): string {
    base = this.truncateName(base);
    const nameToCheck = i === 0 ? base : `${base}${i}`;
    return usedNames.has(nameToCheck)
      ? this.getUniqueSheetName(base, usedNames, i + 1)
      : nameToCheck;
  }

  private truncateName(name: string, maxLength = 29): string {
    return name.length > maxLength ? name.slice(0, maxLength) : name;
  }
}
